/** Tenant brand theming (ported from mockups/assets/ui-kit.js). */
export type Rgb = [number, number, number];

export function hexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return null;
  const n = parseInt(m[1], 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
}

const toHex = (rgb: Rgb): string =>
  '#' + rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

function luminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours (1–21). */
export function contrast(a: string, b: string): number {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return 1;
  const la = luminance(ra);
  const lb = luminance(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export type BrandScale = Record<
  '--brand' | '--brand-600' | '--brand-700' | '--brand-50' | '--brand-100' | '--brand-200' | '--brand-contrast',
  string
>;

/** Full shade scale from one colour. Text on brand becomes dark when white fails 3:1 (large-text AA). */
export function brandScale(hex: string): BrandScale | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const W: Rgb = [255, 255, 255];
  const K: Rgb = [0, 0, 0];
  return {
    '--brand': toHex(rgb),
    '--brand-600': toHex(mix(rgb, K, 0.15)),
    '--brand-700': toHex(mix(rgb, K, 0.3)),
    '--brand-50': toHex(mix(rgb, W, 0.93)),
    '--brand-100': toHex(mix(rgb, W, 0.85)),
    '--brand-200': toHex(mix(rgb, W, 0.7)),
    '--brand-contrast': contrast(hex, '#ffffff') >= 3 ? '#ffffff' : '#0f172a',
  };
}

/** Runtime-applied vars. Tints (--brand-50/100/200) are NOT set inline: CSS derives them from --brand with
 *  color-mix, so the dark theme can use its own tints. Setting them inline would override dark mode. */
const APPLIED = ['--brand', '--brand-600', '--brand-700', '--brand-contrast'] as const;

export function applyBrand(hex: string, el: HTMLElement = document.documentElement): boolean {
  const s = brandScale(hex);
  if (!s) return false;
  for (const k of APPLIED) el.style.setProperty(k, s[k]);
  return true;
}

/* ---------- fonts (5 styles, Plus Jakarta Sans default) ---------- */
export const FONTS = {
  jakarta: { name: 'Plus Jakarta Sans', desc: 'Modern & friendly (default)', family: '"Plus Jakarta Sans"', g: 'Plus+Jakarta+Sans:wght@400;500;600;700;800' },
  inter: { name: 'Inter', desc: 'Clean & neutral', family: '"Inter"', g: 'Inter:wght@400;500;600;700;800' },
  manrope: { name: 'Manrope', desc: 'Geometric & sharp', family: '"Manrope"', g: 'Manrope:wght@400;500;600;700;800' },
  dmsans: { name: 'DM Sans', desc: 'Soft & compact', family: '"DM Sans"', g: 'DM+Sans:wght@400;500;600;700;800' },
  poppins: { name: 'Poppins', desc: 'Bold & rounded', family: '"Poppins"', g: 'Poppins:wght@400;500;600;700;800' },
} as const;
export type FontKey = keyof typeof FONTS;

const loaded = new Set<FontKey>(['jakarta']);
export function loadFont(key: FontKey): void {
  if (loaded.has(key) || typeof document === 'undefined') return;
  loaded.add(key);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${FONTS[key].g}&display=swap`;
  document.head.append(link);
}

export function applyFont(key: FontKey, el: HTMLElement = document.documentElement): void {
  loadFont(key);
  el.style.setProperty('--font', `${FONTS[key].family}, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`);
}

export type ThemeMode = 'light' | 'dark' | 'system';
export function applyTheme(mode: ThemeMode): 'light' | 'dark' {
  const dark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  return dark ? 'dark' : 'light';
}
